import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import {
  ArrowLeft,
  User,
  Building2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Clock,
  Edit3,
  Save,
  X,
  ChevronDown,
  ChevronRight,
  Tag,
  Zap,
  FileText,
  DollarSign,
  Shield,
  Bell,
  BellOff,
  ExternalLink,
  MoreVertical,
  MoreHorizontal,
  Star,
  StarOff,
  Copy,
  Check,
  Send,
  Paperclip,
  Image,
  Smile,
  Link2,
  MessageSquare,
  Globe,
  Activity,
  UserPlus,
  Settings,
  Briefcase,
  Hash,
  AtSign,
  CreditCard,
  AlertCircle,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  RefreshCw,
  Plus,
  Trash2,
  Info,
  Forward,
  Loader2,
  MailOpen,
  MousePointerClick,
  Reply,
  Ban,
  ThumbsDown,
  UserMinus,
  ShoppingCart,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { cn } from '../../../lib/utils';
import WhopStatusCard from '../../../components/whop/WhopStatusCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Channel type configurations
const CHANNEL_TYPES = {
  portal: { label: 'Portal', icon: Globe, color: 'bg-purple-100 text-purple-700 border-purple-200' },
  sms: { label: 'SMS', icon: MessageSquare, color: 'bg-green-100 text-green-700 border-green-200' },
  email: { label: 'Email', icon: Mail, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  internal: { label: 'Internal Note', icon: FileText, color: 'bg-amber-100 text-amber-700 border-amber-200' },
  call: { label: 'Call', icon: Phone, color: 'bg-slate-100 text-slate-700 border-slate-200' },
};

// Activity type icons
const ACTIVITY_ICONS = {
  message: MessageSquare,
  email: Mail,
  sms: MessageSquare,
  call: Phone,
  document: FileText,
  automation: Zap,
  tag: Tag,
  created: UserPlus,
  status: Activity,
  payment: DollarSign,
  portal: Globe,
  dispute: AlertCircle,
  case: Briefcase,
};

// Channel Badge Component
const ChannelBadge = ({ channel, size = 'sm' }) => {
  const config = CHANNEL_TYPES[channel] || CHANNEL_TYPES.portal;
  const Icon = config.icon;
  
  return (
    <Badge 
      variant="outline" 
      className={cn(
        'font-medium',
        config.color,
        size === 'sm' ? 'text-xs px-1.5 py-0' : 'text-xs px-2 py-0.5'
      )}
    >
      <Icon size={size === 'sm' ? 10 : 12} className="mr-1" />
      {config.label}
    </Badge>
  );
};

// Message Bubble Component
// Message Status Icon Component - Shows REAL provider status only
const MessageStatusIcon = ({ status, deliveryLog }) => {
  // Map real status to icon and tooltip
  const statusConfig = {
    queued: { icon: Clock, color: 'text-slate-400', tooltip: 'Queued' },
    sending: { icon: Loader2, color: 'text-blue-400 animate-spin', tooltip: 'Sending' },
    pending: { icon: Clock, color: 'text-slate-400', tooltip: 'Pending' },
    accepted: { icon: Check, color: 'text-blue-500', tooltip: 'Accepted by provider' },
    sent: { icon: Check, color: 'text-blue-500', tooltip: 'Sent to provider' },
    delivered: { icon: CheckCircle, color: 'text-emerald-500', tooltip: 'Delivered' },
    opened: { icon: MailOpen, color: 'text-cyan-500', tooltip: 'Opened' },
    clicked: { icon: MousePointerClick, color: 'text-purple-500', tooltip: 'Link clicked' },
    replied: { icon: Reply, color: 'text-indigo-500', tooltip: 'Replied' },
    bounced: { icon: XCircle, color: 'text-red-500', tooltip: 'Bounced' },
    failed: { icon: AlertCircle, color: 'text-red-500', tooltip: 'Failed to send' },
    rejected: { icon: Ban, color: 'text-red-500', tooltip: 'Rejected' },
    complained: { icon: ThumbsDown, color: 'text-orange-500', tooltip: 'Marked as spam' },
    unsubscribed: { icon: UserMinus, color: 'text-amber-500', tooltip: 'Unsubscribed' },
  };

  // Get the highest-priority status to display
  const displayStatus = status || 'pending';
  const config = statusConfig[displayStatus] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <div className="relative group">
      <Icon size={12} className={config.color} />
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
        {config.tooltip}
        {deliveryLog?.last_event_at && (
          <span className="block text-slate-300 text-[10px]">
            {new Date(deliveryLog.last_event_at).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
};

// Message Details Modal Component
const MessageDetailsModal = ({ isOpen, onClose, message, deliveryLog: initialDeliveryLog }) => {
  const [deliveryLog, setDeliveryLog] = useState(initialDeliveryLog);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch detailed delivery log from API when modal opens
  useEffect(() => {
    const fetchDeliveryLog = async () => {
      if (!isOpen || !initialDeliveryLog?._id) {
        setDeliveryLog(initialDeliveryLog);
        return;
      }

      setIsLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/webhooks/mailgun/delivery-log/${initialDeliveryLog._id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) {
          const data = await res.json();
          setDeliveryLog(data);
        }
      } catch (err) {
        console.error('Failed to fetch delivery log:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeliveryLog();
  }, [isOpen, initialDeliveryLog]);

  if (!isOpen) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
  };

  const eventTimeline = [
    { label: 'Queued', timestamp: deliveryLog?.queued_at },
    { label: 'Sent', timestamp: deliveryLog?.sent_at },
    { label: 'Accepted', timestamp: deliveryLog?.accepted_at },
    { label: 'Delivered', timestamp: deliveryLog?.delivered_at },
    { label: 'Opened', timestamp: deliveryLog?.opened_at },
    { label: 'Clicked', timestamp: deliveryLog?.clicked_at },
    { label: 'Replied', timestamp: deliveryLog?.replied_at },
    { label: 'Bounced', timestamp: deliveryLog?.bounced_at },
    { label: 'Failed', timestamp: deliveryLog?.failed_at },
    { label: 'Rejected', timestamp: deliveryLog?.rejected_at },
    { label: 'Complained', timestamp: deliveryLog?.complained_at },
    { label: 'Unsubscribed', timestamp: deliveryLog?.unsubscribed_at },
  ].filter(e => e.timestamp);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Message Details</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              <span className="ml-2 text-sm text-slate-500">Loading details...</span>
            </div>
          ) : (
            <>
              {/* Basic Info */}
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">From</span>
                  <span className="text-sm font-medium text-slate-900">
                    {deliveryLog?.from_email || message?.senderName || '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">To</span>
                  <span className="text-sm font-medium text-slate-900">
                    {deliveryLog?.recipient_email || '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Direction</span>
                  <Badge variant="outline" className="text-xs">
                    {message?.sender === 'agent' ? 'Outbound' : 'Inbound'}
                  </Badge>
                </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Channel</span>
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                {deliveryLog?.channel || message?.channel || 'Email'}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Date/Time</span>
              <span className="text-sm text-slate-900">
                {formatDate(message?.timestamp || deliveryLog?.created_at)}
              </span>
            </div>
          </div>

          {/* Source & Provider */}
          <div className="border-t border-slate-200 pt-4 space-y-2">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Source & Provider</h4>
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Source</span>
              <span className="text-sm text-slate-900">{deliveryLog?.source || 'manual'}</span>
            </div>
            {deliveryLog?.workflow_id && (
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Workflow</span>
                <Button variant="link" size="sm" className="h-auto p-0 text-blue-600 text-sm">
                  View Workflow
                </Button>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Provider</span>
              <span className="text-sm text-slate-900">{deliveryLog?.provider_used || 'Not tracked'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Provider Message ID</span>
              <span className="text-xs font-mono text-slate-600 truncate max-w-[200px]">
                {deliveryLog?.provider_message_id || '—'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Current Status</span>
              <Badge 
                variant="outline" 
                className={cn(
                  'text-xs',
                  deliveryLog?.status === 'delivered' && 'bg-emerald-50 text-emerald-700',
                  deliveryLog?.status === 'opened' && 'bg-cyan-50 text-cyan-700',
                  deliveryLog?.status === 'failed' && 'bg-red-50 text-red-700',
                  deliveryLog?.status === 'bounced' && 'bg-red-50 text-red-700',
                )}
              >
                {deliveryLog?.status || 'Unknown'}
              </Badge>
            </div>
          </div>

          {/* Email Content (Subject & Body) */}
          {(message?.subject || deliveryLog?.subject) && (
            <div className="border-t border-slate-200 pt-4">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Email Content</h4>
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div>
                  <span className="text-xs text-slate-500">Subject</span>
                  <p className="text-sm font-medium text-slate-900">{message?.subject || deliveryLog?.subject}</p>
                </div>
                {(message?.body_html || deliveryLog?.body_html) && (
                  <div>
                    <span className="text-xs text-slate-500">Body Preview</span>
                    <div 
                      className="text-sm text-slate-700 mt-1 max-h-40 overflow-y-auto prose prose-sm"
                      dangerouslySetInnerHTML={{ __html: message?.body_html || deliveryLog?.body_html || '' }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Event Timeline */}
          <div className="border-t border-slate-200 pt-4">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Event Timeline</h4>
            {eventTimeline.length > 0 ? (
              <div className="space-y-2">
                {eventTimeline.map((event, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                    <span className="text-sm text-slate-700 flex-1">{event.label}</span>
                    <span className="text-xs text-slate-500">{formatDate(event.timestamp)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 italic">No events recorded yet</p>
            )}
          </div>

          {/* Error if any */}
          {deliveryLog?.error_message && (
            <div className="border-t border-slate-200 pt-4">
              <h4 className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-2">Error</h4>
              <p className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {deliveryLog.error_message}
              </p>
            </div>
          )}
            </>
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50">
          <Button variant="outline" onClick={onClose} className="w-full">Close</Button>
        </div>
      </div>
    </div>
  );
};

const MessageBubble = ({ message, deliveryLog, onShowDetails }) => {
  const isAgent = message.sender === 'agent';
  const [showMenu, setShowMenu] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const isEmail = message.channel === 'email';
  
  const formatTimestamp = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) +
      ' · ' + date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Extract text from HTML
  const extractTextFromHtml = (html) => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  // Get email body preview
  const getEmailBodyPreview = () => {
    if (message.body_html) {
      return extractTextFromHtml(message.body_html);
    }
    if (message.content && !message.content.startsWith('Subject:') && !message.content.startsWith('Workflow email sent:')) {
      return message.content;
    }
    return '';
  };

  const emailBodyPreview = isEmail ? getEmailBodyPreview() : '';

  return (
    <>
      <div className={cn('flex gap-3 mb-4 group', isAgent && 'flex-row-reverse')}>
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarFallback className={cn(
            'text-xs',
            isAgent ? 'bg-teal-100 text-teal-700' : 'bg-slate-200 text-slate-700'
          )}>
            {message.senderName?.split(' ').map(n => n[0]).join('') || '?'}
          </AvatarFallback>
        </Avatar>
        
        <div className={cn('max-w-[75%]', isAgent && 'items-end')}>
          <div className={cn('flex items-center gap-2 mb-1', isAgent && 'flex-row-reverse')}>
            <span className="text-xs font-medium text-slate-700">{message.senderName}</span>
            <ChannelBadge channel={message.channel} />
            {/* Status icon for outbound messages */}
            {isAgent && isEmail && (
              <MessageStatusIcon status={deliveryLog?.status || message.status} deliveryLog={deliveryLog} />
            )}
          </div>
          
          <div className="relative">
            {/* Email-specific rendering with subject and body */}
            {isEmail && message.subject ? (
              <div className={cn(
                'rounded-xl overflow-hidden border',
                isAgent 
                  ? 'bg-white border-teal-200' 
                  : 'bg-white border-slate-200',
              )}>
                {/* Email Header */}
                <div className={cn(
                  'px-4 py-2 border-b',
                  isAgent ? 'bg-teal-50 border-teal-100' : 'bg-slate-50 border-slate-100'
                )}>
                  <p className={cn(
                    'text-sm font-semibold',
                    isAgent ? 'text-teal-900' : 'text-slate-900'
                  )}>
                    Subject: {message.subject}
                  </p>
                  {deliveryLog?.from_email && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      From: {deliveryLog.from_name || 'FundElite'} &lt;{deliveryLog.from_email}&gt;
                    </p>
                  )}
                </div>
                
                {/* Email Body Preview */}
                <div className="px-4 py-3">
                  <p className="text-sm text-slate-700 leading-relaxed line-clamp-6 whitespace-pre-wrap">
                    {emailBodyPreview || message.content}
                  </p>
                  {emailBodyPreview && emailBodyPreview.length > 300 && (
                    <button 
                      onClick={() => setShowDetails(true)}
                      className="text-xs text-teal-600 hover:text-teal-700 mt-2 flex items-center gap-1"
                    >
                      <ExternalLink size={12} />
                      View full email
                    </button>
                  )}
                </div>
                
                {/* Status Badge */}
                {(deliveryLog?.status || message.status) && (
                  <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
                    <div className="flex items-center justify-between">
                      <span className={cn(
                        'text-xs px-2 py-0.5 rounded-full font-medium',
                        (deliveryLog?.status || message.status) === 'delivered' && 'bg-emerald-100 text-emerald-700',
                        (deliveryLog?.status || message.status) === 'opened' && 'bg-cyan-100 text-cyan-700',
                        (deliveryLog?.status || message.status) === 'clicked' && 'bg-purple-100 text-purple-700',
                        (deliveryLog?.status || message.status) === 'accepted' && 'bg-blue-100 text-blue-700',
                        (deliveryLog?.status || message.status) === 'bounced' && 'bg-red-100 text-red-700',
                        (deliveryLog?.status || message.status) === 'failed' && 'bg-red-100 text-red-700',
                        !['delivered', 'opened', 'clicked', 'accepted', 'bounced', 'failed'].includes(deliveryLog?.status || message.status) && 'bg-slate-100 text-slate-700'
                      )}>
                        {(deliveryLog?.status || message.status || 'sent').charAt(0).toUpperCase() + (deliveryLog?.status || message.status || 'sent').slice(1)}
                      </span>
                      <button 
                        onClick={() => setShowDetails(true)}
                        className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                      >
                        <Info size={12} />
                        Details
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Regular message bubble for non-email or emails without subject */
              <div className={cn(
                'rounded-2xl px-4 py-2.5',
                isAgent 
                  ? 'bg-teal-500 text-white rounded-tr-sm' 
                  : 'bg-slate-100 text-slate-800 rounded-tl-sm',
                message.channel === 'internal' && 'bg-amber-50 text-amber-900 border border-amber-200'
              )}>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
              </div>
            )}
            
            {/* Three-dot menu */}
            <div className={cn(
              'absolute top-0 opacity-0 group-hover:opacity-100 transition-opacity',
              isAgent ? 'left-0 -translate-x-full pl-1' : 'right-0 translate-x-full pr-1'
            )}>
              <DropdownMenu open={showMenu} onOpenChange={setShowMenu}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6">
                    <MoreHorizontal size={14} className="text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={isAgent ? 'start' : 'end'}>
                  <DropdownMenuItem onClick={() => { setShowDetails(true); setShowMenu(false); }}>
                    <Info size={14} className="mr-2" />
                    Details
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Forward size={14} className="mr-2" />
                    Forward Email
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Forward size={14} className="mr-2" />
                    Forward Thread
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          
          <p className={cn('text-xs text-slate-400 mt-1', isAgent && 'text-right')}>
            {formatTimestamp(message.timestamp)}
          </p>
        </div>
      </div>
      
      {/* Message Details Modal */}
      <MessageDetailsModal
        isOpen={showDetails}
        onClose={() => setShowDetails(false)}
        message={message}
        deliveryLog={deliveryLog}
      />
    </>
  );
};

// Editable Field Component
const EditableField = ({ label, value, onChange, type = 'text', placeholder, disabled }) => {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-slate-500">{label}</Label>
      <Input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="h-9 text-sm"
      />
    </div>
  );
};

// Collapsible Section Component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} className="text-slate-500" />}
          <span className="text-sm font-medium text-slate-700">{title}</span>
        </div>
        <ChevronRight 
          size={14} 
          className={cn('text-slate-400 transition-transform', isOpen && 'rotate-90')} 
        />
      </button>
      {isOpen && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
};

// Activity Timeline Item with Source Labels
const ActivityItem = ({ activity }) => {
  // Map activity types to icons - include Whop-specific types
  const EXTENDED_ACTIVITY_ICONS = {
    ...ACTIVITY_ICONS,
    whop_event: ShoppingCart,
    membership: CreditCard,
    purchase: ShoppingCart,
    discovery: UserPlus,
    system: Activity,
    portal: Globe,
    user: User,
  };
  
  const IconComponent = EXTENDED_ACTIVITY_ICONS[activity.type] || EXTENDED_ACTIVITY_ICONS[activity.activity_type] || Activity;
  
  // Source label colors
  const SOURCE_COLORS = {
    'Whop': 'bg-emerald-100 text-emerald-700',
    'Webhook': 'bg-blue-100 text-blue-700',
    'Sync': 'bg-purple-100 text-purple-700',
    'Polling': 'bg-amber-100 text-amber-700',
    'Manual': 'bg-slate-100 text-slate-700',
    'Workflow': 'bg-pink-100 text-pink-700',
    'Mailgun': 'bg-orange-100 text-orange-700',
    'Portal': 'bg-cyan-100 text-cyan-700',
    'System': 'bg-slate-100 text-slate-700',
  };
  
  const sourceColorClass = SOURCE_COLORS[activity.sourceLabel] || 'bg-slate-100 text-slate-600';
  
  // Determine icon background color based on activity type
  const getIconBgColor = () => {
    if (activity.type === 'payment' || activity.type === 'purchase') return 'bg-emerald-100';
    if (activity.type === 'automation' || activity.type === 'workflow') return 'bg-purple-100';
    if (activity.type === 'created' || activity.type === 'user') return 'bg-blue-100';
    if (activity.type === 'whop_event' || activity.type === 'membership') return 'bg-emerald-50';
    if (activity.type === 'portal') return 'bg-cyan-100';
    return 'bg-slate-100';
  };
  
  const getIconTextColor = () => {
    if (activity.type === 'payment' || activity.type === 'purchase') return 'text-emerald-600';
    if (activity.type === 'automation' || activity.type === 'workflow') return 'text-purple-600';
    if (activity.type === 'created' || activity.type === 'user') return 'text-blue-600';
    if (activity.type === 'whop_event' || activity.type === 'membership') return 'text-emerald-600';
    if (activity.type === 'portal') return 'text-cyan-600';
    return 'text-slate-500';
  };
  
  return (
    <div className="flex items-start gap-3 py-2" data-testid="activity-item">
      <div className={cn(
        'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0',
        getIconBgColor()
      )}>
        <IconComponent size={12} className={getIconTextColor()} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm text-slate-700">{activity.text}</p>
          {/* Source label badge */}
          {activity.sourceLabel && (
            <span className={cn(
              'text-[10px] px-1.5 py-0.5 rounded font-medium',
              sourceColorClass
            )}>
              {activity.sourceLabel}
            </span>
          )}
          {activity.channel && <ChannelBadge channel={activity.channel} />}
        </div>
        {/* Show description if different from text */}
        {activity.description && activity.description !== activity.text && (
          <p className="text-xs text-slate-500 mt-0.5">{activity.description}</p>
        )}
        <p className="text-xs text-slate-400">{activity.time}</p>
      </div>
    </div>
  );
};

// Need ShoppingCart for Whop activities - imported at top of file

// Main Platform Contact Workspace Page
const PlatformContactWorkspacePage = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const messageEndRef = useRef(null);

  // State - Initialize with null/empty, will be populated from API
  const [contact, setContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [activeChannel, setActiveChannel] = useState('portal');
  const [activeTab, setActiveTab] = useState('contact'); // 'contact' | 'company'
  const [isEditing, setIsEditing] = useState(false);
  const [editedContact, setEditedContact] = useState({});
  const [hideEmpty, setHideEmpty] = useState(false);
  const [agencyName, setAgencyName] = useState('');

  // Fetch contact data from API
  useEffect(() => {
    const fetchContactData = async () => {
      if (!contactId || !token) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch contact details
        const contactRes = await fetch(`${API_URL}/api/platform/contacts/${contactId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!contactRes.ok) {
          throw new Error('Failed to fetch contact');
        }
        
        const contactData = await contactRes.json();
        
        // Transform API response to expected format
        const transformedContact = {
          id: contactData.id || contactId,
          firstName: contactData.first_name || '',
          lastName: contactData.last_name || '',
          email: contactData.email || '',
          phone: contactData.phone || '',
          phoneRaw: contactData.phone?.replace(/[^0-9+]/g, '') || '',
          whopUsername: contactData.whop_username || '',  // Whop username stored separately
          whopUserId: contactData.whop_user_id || '',
          discordUsername: contactData.discord_username || '',
          discordId: contactData.discord_id || '',
          dateOfBirth: contactData.date_of_birth || '',
          source: contactData.source || contactData.contact_source || 'Manual Entry',
          contactType: contactData.contact_type || 'lead',
          status: contactData.status || 'active',
          company: {
            name: contactData.company_name || contactData.business_name || '',
            phone: contactData.company_phone || '',
            email: contactData.company_email || '',
            website: contactData.website || '',
            address: contactData.company_address || '',
          },
          address: {
            street: contactData.street_address || contactData.address || '',
            city: contactData.city || '',
            state: contactData.state || '',
            zip: contactData.zip_code || contactData.postal_code || '',
          },
          ssn_last4: contactData.ssn_last4 || '',
          tags: contactData.tags || [],
          owner: contactData.owner || null,
          followers: contactData.followers || [],
          dnd: {
            all: contactData.dnd_all || false,
            emails: contactData.dnd_email || false,
            sms: contactData.dnd_sms || false,
            calls: contactData.dnd_calls || false,
            voicemails: contactData.dnd_voicemails || false,
            incomingDnd: contactData.dnd_inbound || false,
          },
          automations: contactData.active_automations || [],
          cases: contactData.cases || [],
          opportunities: contactData.opportunities || [],
          createdAt: contactData.created_at || new Date().toISOString(),
          createdBy: contactData.created_by || 'System',
          lastActivity: contactData.last_activity_at || contactData.updated_at || null,
          agencyId: contactData.agency_id || null,
          agencyName: contactData.agency_name || '',
          customFields: contactData.custom_fields || {},
          whopMappingStatus: contactData.whop_mapping_status || '',
          totalWhopSpend: contactData.total_whop_spend || 0,
          whopCustomFields: contactData.whop_custom_fields || {},
          customFieldsSyncedAt: contactData.custom_fields_synced_at || null,
          whopFullName: contactData.whop_full_name || '',
          whopPhone: contactData.whop_phone || '',
          lastWhopSync: contactData.last_whop_sync || contactData.whop_synced_at || null,
        };
        
        setContact(transformedContact);
        setEditedContact(transformedContact);
        setAgencyName(contactData.agency_name || '');
        
        // Fetch messages/conversations if available
        try {
          const messagesRes = await fetch(`${API_URL}/api/platform/contacts/${contactId}/messages`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (messagesRes.ok) {
            const messagesData = await messagesRes.json();
            // Transform messages from API to component format
            const transformedMessages = (messagesData.messages || []).map(msg => ({
              id: msg.id || msg._id,
              content: msg.content || msg.subject || '',
              subject: msg.subject,
              body_html: msg.body_html,
              channel: msg.channel || 'email',
              timestamp: new Date(msg.timestamp || msg.created_at || Date.now()),
              sender: msg.sender_type === 'contact' ? 'contact' : 'agent',
              senderName: msg.sender_type === 'contact' 
                ? `${transformedContact.firstName} ${transformedContact.lastName}`.trim() || 'Contact'
                : msg.sender_name || 'Agent',
              delivery_log_id: msg.delivery_log_id,
              provider_message_id: msg.provider_message_id,
              status: msg.status,
            }));
            // Reverse to show oldest first (chronological order)
            setMessages(transformedMessages.reverse());
          }
        } catch (msgErr) {
          console.log('No messages available:', msgErr);
        }
        
        // Fetch activity timeline - use the unified activities endpoint
        try {
          // Use the new platform/contacts activities endpoint which includes Whop events
          const activitiesRes = await fetch(
            `${API_URL}/api/platform/contacts/${contactId}/activities?limit=100`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (activitiesRes.ok) {
            const activitiesData = await activitiesRes.json();
            const activityList = activitiesData.activities || [];
            
            // Transform activities with source labels for UI
            setActivities(activityList.map(act => ({
              id: act.id,
              type: act.activity_type || act.type || 'status',
              eventType: act.event_type,
              channel: act.channel || null,
              text: act.title || act.description || act.text || act.message || '',
              description: act.description,
              time: formatRelativeTime(act.created_at || act.timestamp),
              timestamp: new Date(act.created_at || act.timestamp || Date.now()),
              source: act.source,
              sourceLabel: act.source_label || act.source || 'System',
              metadata: act.metadata || {},
            })));
          } else {
            // Fallback: try legacy endpoint
            const legacyEndpoint = transformedContact.agencyId 
              ? `${API_URL}/api/agencies/${transformedContact.agencyId}/contacts/${contactId}/activity`
              : null;
            
            if (legacyEndpoint) {
              const legacyRes = await fetch(legacyEndpoint, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (legacyRes.ok) {
                const legacyData = await legacyRes.json();
                setActivities((legacyData.activities || []).map(act => ({
                  id: act.id,
                  type: act.activity_type || act.type || 'status',
                  channel: act.channel || null,
                  text: act.description || act.text || act.message || '',
                  time: formatRelativeTime(act.created_at || act.timestamp),
                  timestamp: new Date(act.created_at || act.timestamp || Date.now()),
                  source: 'legacy',
                  sourceLabel: 'System',
                })));
              }
            }
          }
        } catch (actErr) {
          console.log('No activities endpoint available:', actErr);
        }
        
      } catch (err) {
        console.error('Error fetching contact:', err);
        setError(err.message || 'Failed to load contact');
      } finally {
        setLoading(false);
      }
    };
    
    fetchContactData();
  }, [contactId, token]);

  // Helper to format relative time
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // Initialize edited contact when contact changes
  useEffect(() => {
    if (contact) {
      setEditedContact({ ...contact });
    }
  }, [contact]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!messageInput.trim()) return;
    if (activeChannel === 'email' && !emailSubject.trim()) return;

    setIsSending(true);
    try {
      const response = await fetch(`${API_URL}/api/platform/contacts/${contactId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          channel: activeChannel,
          content: messageInput,
          subject: activeChannel === 'email' ? (emailSubject || 'Message from FundElite') : null,
          body_html: activeChannel === 'email' ? `<p>${messageInput.replace(/\n/g, '<br/>')}</p>` : null,
          sender_type: 'agent',
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        const newMessage = {
          id: result.message?.id || result.delivery_log_id || `msg_${Date.now()}`,
          content: messageInput,
          subject: activeChannel === 'email' ? emailSubject : null,
          channel: activeChannel,
          timestamp: new Date(),
          sender: 'agent',
          senderName: 'You',
          status: result.status || 'sent',
          provider_message_id: result.provider_message_id,
          delivery_log_id: result.delivery_log_id,
        };
        setMessages(prev => [...prev, newMessage]);
        setMessageInput('');
        setEmailSubject('');
      } else {
        alert(result.detail || result.error || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleSaveContact = async () => {
    try {
      // Transform edited contact back to API format
      const updatePayload = {
        first_name: editedContact.firstName,
        last_name: editedContact.lastName,
        email: editedContact.email,
        phone: editedContact.phone,
        date_of_birth: editedContact.dateOfBirth,
        contact_type: editedContact.contactType,
        status: editedContact.status,
        company_name: editedContact.company?.name,
        street_address: editedContact.address?.street,
        city: editedContact.address?.city,
        state: editedContact.address?.state,
        zip_code: editedContact.address?.zip,
        tags: editedContact.tags,
        dnd_all: editedContact.dnd?.all,
        dnd_email: editedContact.dnd?.emails,
        dnd_sms: editedContact.dnd?.sms,
        dnd_calls: editedContact.dnd?.calls,
      };
      
      const response = await fetch(`${API_URL}/api/platform/contacts/${contactId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatePayload),
      });
      
      if (!response.ok) {
        throw new Error('Failed to save contact');
      }
      
      setContact(editedContact);
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving contact:', err);
      alert('Failed to save contact. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditedContact({ ...contact });
    setIsEditing(false);
  };

  const handleToggleDnd = (field) => {
    setEditedContact(prev => ({
      ...prev,
      dnd: {
        ...prev.dnd,
        [field]: !prev.dnd[field],
      },
    }));
  };

  const handleRemoveTag = (tagToRemove) => {
    setEditedContact(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tagToRemove),
    }));
  };

  // Handle loading state
  if (loading) {
    return (
      <div className="h-[calc(100vh-64px)] flex items-center justify-center bg-white">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-400 mx-auto mb-3" />
          <p className="text-slate-600">Loading contact...</p>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error || !contact) {
    return (
      <div className="h-[calc(100vh-64px)] flex items-center justify-center bg-white">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Contact Not Found</h2>
          <p className="text-slate-600 mb-4">{error || 'The contact you are looking for does not exist or you do not have access to it.'}</p>
          <Button onClick={() => navigate('/platform/contacts')} className="gap-2">
            <ArrowLeft size={16} />
            Back to Contacts
          </Button>
        </div>
      </div>
    );
  }

  const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unknown Contact';
  const initials = fullName.split(' ').filter(n => n).map(n => n[0]).join('').toUpperCase() || '?';

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-white" data-testid="platform-contact-workspace-page">
      {/* Top Header */}
      <div className="px-6 py-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="gap-2"
              data-testid="back-btn"
            >
              <ArrowLeft size={16} />
              Back
            </Button>
            <div className="h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-gradient-to-br from-slate-700 to-slate-800 text-white">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div>
                <h1 className="text-lg font-bold text-slate-900" data-testid="contact-workspace-header">
                  Contact Workspace
                </h1>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-slate-500">{fullName}</p>
                  <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                    <Building2 size={10} className="mr-1" />
                    {agencyName}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Owner */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Owner:</span>
              <Avatar className="h-6 w-6">
                <AvatarFallback className="text-xs bg-teal-100 text-teal-700">
                  {contact.owner?.name?.split(' ').map(n => n[0]).join('') || '?'}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm text-slate-700">{contact.owner?.name || 'Unassigned'}</span>
            </div>
            
            <div className="h-6 w-px bg-slate-200" />
            
            {/* Followers */}
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-500">Followers:</span>
              {(contact.followers || []).slice(0, 3).map((follower, i) => (
                <Avatar key={follower.id} className={cn('h-6 w-6', i > 0 && '-ml-1')}>
                  <AvatarFallback className="text-xs bg-slate-100 text-slate-600">
                    {follower.name?.split(' ').map(n => n[0]).join('') || '?'}
                  </AvatarFallback>
                </Avatar>
              ))}
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-full">
                <Plus size={12} className="text-slate-400" />
              </Button>
            </div>
            
            <div className="h-6 w-px bg-slate-200" />
            
            {/* Quick Actions */}
            <Button variant="outline" size="sm" className="gap-2">
              <Phone size={14} />
              Call
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <Mail size={14} />
              Email
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreVertical size={16} className="text-slate-400" />
            </Button>
          </div>
        </div>
      </div>

      {/* Three Column Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Column - Contact Record / Editor */}
        <div className="w-96 border-r border-slate-200 flex flex-col bg-white overflow-y-auto">
          {/* Contact/Company Tabs */}
          <div className="flex border-b border-slate-200 sticky top-0 bg-white z-10">
            <button
              onClick={() => setActiveTab('contact')}
              className={cn(
                'flex-1 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'contact'
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              )}
            >
              <User size={14} className="inline mr-2" />
              Contact
            </button>
            <button
              onClick={() => setActiveTab('company')}
              className={cn(
                'flex-1 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'company'
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              )}
            >
              <Building2 size={14} className="inline mr-2" />
              Company
            </button>
          </div>

          {/* Edit/Save Controls */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-slate-50">
            <label className="flex items-center gap-2 text-xs text-slate-500">
              <input
                type="checkbox"
                checked={hideEmpty}
                onChange={(e) => setHideEmpty(e.target.checked)}
                className="rounded border-slate-300"
              />
              Hide empty fields
            </label>
            {isEditing ? (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSaveContact} className="bg-teal-600 hover:bg-teal-700">
                  <Save size={12} className="mr-1" />
                  Save
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setIsEditing(true)}>
                <Edit3 size={12} className="mr-1" />
                Edit
              </Button>
            )}
          </div>

          {activeTab === 'contact' ? (
            <div className="flex-1">
              {/* General Info */}
              <CollapsibleSection title="General Info" icon={User} defaultOpen={true}>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <EditableField
                      label="First Name"
                      value={editedContact.firstName}
                      onChange={(v) => setEditedContact(p => ({ ...p, firstName: v }))}
                      disabled={!isEditing}
                    />
                    <EditableField
                      label="Last Name"
                      value={editedContact.lastName}
                      onChange={(v) => setEditedContact(p => ({ ...p, lastName: v }))}
                      disabled={!isEditing}
                    />
                  </div>
                  <EditableField
                    label="Email"
                    value={editedContact.email}
                    onChange={(v) => setEditedContact(p => ({ ...p, email: v }))}
                    type="email"
                    disabled={!isEditing}
                  />
                  <EditableField
                    label="Phone"
                    value={editedContact.phone}
                    onChange={(v) => setEditedContact(p => ({ ...p, phone: v }))}
                    disabled={!isEditing}
                  />
                  <EditableField
                    label="Date of Birth"
                    value={editedContact.dateOfBirth}
                    onChange={(v) => setEditedContact(p => ({ ...p, dateOfBirth: v }))}
                    type="date"
                    disabled={!isEditing}
                  />
                </div>
              </CollapsibleSection>

              {/* Whop Identity - Show username, discord, and spend */}
              {(contact.whopUsername || contact.whopUserId || contact.discordUsername) && (
                <CollapsibleSection title="Whop Identity" icon={ShoppingCart} defaultOpen={true}>
                  <div className="space-y-3">
                    {contact.whopUsername && (
                      <div className="flex justify-between items-center py-1.5 px-2 bg-slate-50 rounded">
                        <span className="text-xs text-slate-500">Whop Username</span>
                        <span className="text-sm font-medium text-slate-700">@{contact.whopUsername}</span>
                      </div>
                    )}
                    {contact.discordUsername && (
                      <div className="flex justify-between items-center py-1.5 px-2 bg-slate-50 rounded">
                        <span className="text-xs text-slate-500">Discord</span>
                        <span className="text-sm font-medium text-slate-700">{contact.discordUsername}</span>
                      </div>
                    )}
                    {contact.whopUserId && (
                      <div className="flex justify-between items-center py-1.5 px-2 bg-slate-50 rounded">
                        <span className="text-xs text-slate-500">Whop User ID</span>
                        <span className="text-xs font-mono text-slate-600" title={contact.whopUserId}>
                          {contact.whopUserId.slice(0, 20)}...
                        </span>
                      </div>
                    )}
                    {contact.totalWhopSpend > 0 && (
                      <div className="flex justify-between items-center py-1.5 px-2 bg-emerald-50 rounded">
                        <span className="text-xs text-emerald-600">Total Whop Spend</span>
                        <span className="text-sm font-medium text-emerald-700">
                          ${(contact.totalWhopSpend || 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {contact.whopMappingStatus && (
                      <div className="flex justify-between items-center py-1.5 px-2 bg-slate-50 rounded">
                        <span className="text-xs text-slate-500">Mapping Status</span>
                        <Badge 
                          variant="outline" 
                          className={cn(
                            'text-xs',
                            contact.whopMappingStatus === 'mapped' 
                              ? 'bg-emerald-100 text-emerald-700 border-emerald-200' 
                              : 'bg-amber-100 text-amber-700 border-amber-200'
                          )}
                        >
                          {contact.whopMappingStatus}
                        </Badge>
                      </div>
                    )}
                    {contact.whopFullName && contact.whopFullName !== `${contact.firstName} ${contact.lastName}`.trim() && (
                      <div className="flex justify-between items-center py-1.5 px-2 bg-blue-50 rounded">
                        <span className="text-xs text-blue-600">Full Name from Whop</span>
                        <span className="text-sm font-medium text-blue-700">{contact.whopFullName}</span>
                      </div>
                    )}
                    {contact.whopPhone && contact.whopPhone !== contact.phone && (
                      <div className="flex justify-between items-center py-1.5 px-2 bg-blue-50 rounded">
                        <span className="text-xs text-blue-600">Phone from Whop</span>
                        <span className="text-sm font-medium text-blue-700">{contact.whopPhone}</span>
                      </div>
                    )}
                    {contact.lastWhopSync && (
                      <div className="flex justify-between items-center py-1.5 px-2 bg-slate-50 rounded">
                        <span className="text-xs text-slate-500">Last Whop Sync</span>
                        <span className="text-xs text-slate-600">
                          {new Date(contact.lastWhopSync).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </CollapsibleSection>
              )}

              {/* Whop Custom Fields */}
              {contact.whopCustomFields && Object.keys(contact.whopCustomFields).length > 0 && (
                <CollapsibleSection title="Whop Custom Fields" icon={Tag} defaultOpen={true}>
                  <div className="space-y-2" data-testid="whop-custom-fields-section">
                    {Object.entries(contact.whopCustomFields).map(([key, fieldData]) => {
                      const displayLabel = typeof fieldData === 'object' ? fieldData.label : key;
                      const displayValue = typeof fieldData === 'object' ? fieldData.value : fieldData;
                      
                      return (
                        <div key={key} className="flex justify-between items-center py-1.5 px-2 bg-slate-50 rounded">
                          <span className="text-xs text-slate-500">{displayLabel}</span>
                          <span className="text-sm font-medium text-slate-700">{displayValue}</span>
                        </div>
                      );
                    })}
                    {contact.customFieldsSyncedAt && (
                      <p className="text-xs text-slate-400 pt-1">
                        Last synced: {new Date(contact.customFieldsSyncedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </CollapsibleSection>
              )}

              {/* Additional Info */}
              <CollapsibleSection title="Additional Info" icon={FileText} defaultOpen={false}>
                <div className="space-y-3">
                  <EditableField
                    label="Contact Source"
                    value={editedContact.source}
                    onChange={(v) => setEditedContact(p => ({ ...p, source: v }))}
                    disabled={!isEditing}
                  />
                  <EditableField
                    label="Contact Type"
                    value={editedContact.contactType}
                    onChange={(v) => setEditedContact(p => ({ ...p, contactType: v }))}
                    disabled={!isEditing}
                  />
                  <EditableField
                    label="Street Address"
                    value={editedContact.address?.street}
                    onChange={(v) => setEditedContact(p => ({ ...p, address: { ...p.address, street: v } }))}
                    disabled={!isEditing}
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <EditableField
                      label="City"
                      value={editedContact.address?.city}
                      onChange={(v) => setEditedContact(p => ({ ...p, address: { ...p.address, city: v } }))}
                      disabled={!isEditing}
                    />
                    <EditableField
                      label="State"
                      value={editedContact.address?.state}
                      onChange={(v) => setEditedContact(p => ({ ...p, address: { ...p.address, state: v } }))}
                      disabled={!isEditing}
                    />
                    <EditableField
                      label="ZIP"
                      value={editedContact.address?.zip}
                      onChange={(v) => setEditedContact(p => ({ ...p, address: { ...p.address, zip: v } }))}
                      disabled={!isEditing}
                    />
                  </div>
                </div>
              </CollapsibleSection>

              {/* Tags */}
              <CollapsibleSection title="Tags" icon={Tag} defaultOpen={true}>
                <div className="flex flex-wrap gap-1.5">
                  {editedContact.tags?.map((tag) => (
                    <Badge key={tag} variant="outline" className="bg-slate-50 text-slate-700 pr-1">
                      {tag}
                      {isEditing && (
                        <button 
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-1 p-0.5 hover:bg-slate-200 rounded"
                        >
                          <X size={10} />
                        </button>
                      )}
                    </Badge>
                  ))}
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-slate-500">
                    <Plus size={10} className="mr-1" />
                    Add
                  </Button>
                </div>
              </CollapsibleSection>

              {/* Whop Membership Status (Phase 5 & 6) */}
              <CollapsibleSection title="Whop Membership" icon={CreditCard} defaultOpen={true}>
                <WhopStatusCard
                  contactId={contact.id}
                  contactEmail={contact.email}
                  agencyId={contact.agencyId}
                  token={token}
                  onUpdate={() => {
                    // Refetch contact data
                    const fetchUpdatedContact = async () => {
                      try {
                        const res = await fetch(`${API_URL}/api/platform/contacts/${contactId}`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        if (res.ok) {
                          const data = await res.json();
                          // Transform and update contact
                          const transformedContact = {
                            id: data.id || contactId,
                            firstName: data.first_name || '',
                            lastName: data.last_name || '',
                            email: data.email || '',
                            phone: data.phone || '',
                            status: data.status || 'active',
                            contactType: data.contact_type || 'lead',
                            agencyId: data.agency_id || null,
                            agencyName: data.agency_name || '',
                            // ... keep existing transformations
                            ...contact,
                          };
                          setContact(prev => ({ ...prev, ...data }));
                        }
                      } catch (err) {
                        console.error('Failed to refetch contact:', err);
                      }
                    };
                    fetchUpdatedContact();
                  }}
                />
              </CollapsibleSection>

              {/* Active Automations */}
              <CollapsibleSection title="Active Automations" icon={Zap} defaultOpen={true}>
                <div className="space-y-2">
                  {(contact.automations || []).length > 0 ? (contact.automations || []).map((auto) => (
                    <div 
                      key={auto.id}
                      className={cn(
                        'p-2.5 rounded-lg border',
                        auto.status === 'active' 
                          ? 'bg-purple-50 border-purple-200' 
                          : 'bg-slate-50 border-slate-200'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Zap size={12} className={auto.status === 'active' ? 'text-purple-500' : 'text-slate-400'} />
                          <span className="text-sm font-medium text-slate-700">{auto.name}</span>
                        </div>
                        <Badge variant="outline" className={cn(
                          'text-xs',
                          auto.status === 'active' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
                        )}>
                          {auto.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Step {auto.step} of {auto.totalSteps}</p>
                    </div>
                  )) : (
                    <p className="text-sm text-slate-500 py-2">No active automations</p>
                  )}
                </div>
              </CollapsibleSection>

              {/* Cases / Service Status */}
              <CollapsibleSection title="Cases / Service Status" icon={Briefcase} defaultOpen={true}>
                <div className="space-y-2">
                  {(contact.cases || []).length > 0 ? (contact.cases || []).map((caseItem) => (
                    <div key={caseItem.id} className="p-2.5 bg-teal-50 rounded-lg border border-teal-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-teal-900">{caseItem.name}</span>
                        <Badge variant="outline" className="bg-teal-100 text-teal-700 text-xs">
                          {caseItem.status}
                        </Badge>
                      </div>
                      <div className="w-full h-1.5 bg-teal-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-teal-500 rounded-full"
                          style={{ width: `${caseItem.progress || 0}%` }}
                        />
                      </div>
                      <p className="text-xs text-teal-600 mt-1">{caseItem.progress || 0}% complete</p>
                    </div>
                  )) : (
                    <p className="text-sm text-slate-500 py-2">No active cases</p>
                  )}
                </div>
              </CollapsibleSection>

              {/* DND Settings */}
              <CollapsibleSection title="Do Not Disturb" icon={BellOff} defaultOpen={false}>
                <div className="space-y-2">
                  {[
                    { key: 'all', label: 'DND All Channels' },
                    { key: 'emails', label: 'Emails' },
                    { key: 'sms', label: 'Text Messages' },
                    { key: 'calls', label: 'Calls & Voicemails' },
                    { key: 'incomingDnd', label: 'Incoming DND' },
                  ].map((item) => (
                    <div key={item.key} className="flex items-center justify-between py-1">
                      <span className="text-sm text-slate-600">{item.label}</span>
                      <button
                        onClick={() => isEditing && handleToggleDnd(item.key)}
                        className={cn(
                          'w-10 h-5 rounded-full transition-colors relative',
                          editedContact.dnd?.[item.key] ? 'bg-red-500' : 'bg-slate-200',
                          !isEditing && 'opacity-60 cursor-not-allowed'
                        )}
                      >
                        <div className={cn(
                          'absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform',
                          editedContact.dnd?.[item.key] ? 'translate-x-5' : 'translate-x-0.5'
                        )} />
                      </button>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>

              {/* Source / Creation Data */}
              <CollapsibleSection title="Source & Metadata" icon={Activity} defaultOpen={false}>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Created By</span>
                    <span className="text-slate-700">{contact.createdBy || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Source</span>
                    <span className="text-slate-700">{contact.source || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Created At</span>
                    <span className="text-slate-700">{contact.createdAt ? new Date(contact.createdAt).toLocaleDateString() : '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Last Activity</span>
                    <span className="text-slate-700">{contact.lastActivity ? new Date(contact.lastActivity).toLocaleDateString() : '-'}</span>
                  </div>
                  <Button variant="outline" size="sm" className="w-full mt-2 text-xs">
                    View Audit Log
                  </Button>
                </div>
              </CollapsibleSection>
            </div>
          ) : (
            <div className="flex-1">
              {/* Company Info */}
              <CollapsibleSection title="Company Details" icon={Building2} defaultOpen={true}>
                <div className="space-y-3">
                  <EditableField
                    label="Company Name"
                    value={editedContact.company?.name}
                    onChange={(v) => setEditedContact(p => ({ ...p, company: { ...p.company, name: v } }))}
                    disabled={!isEditing}
                  />
                  <EditableField
                    label="Phone"
                    value={editedContact.company?.phone}
                    onChange={(v) => setEditedContact(p => ({ ...p, company: { ...p.company, phone: v } }))}
                    disabled={!isEditing}
                  />
                  <EditableField
                    label="Email"
                    value={editedContact.company?.email}
                    onChange={(v) => setEditedContact(p => ({ ...p, company: { ...p.company, email: v } }))}
                    type="email"
                    disabled={!isEditing}
                  />
                  <EditableField
                    label="Website"
                    value={editedContact.company?.website}
                    onChange={(v) => setEditedContact(p => ({ ...p, company: { ...p.company, website: v } }))}
                    disabled={!isEditing}
                  />
                  <EditableField
                    label="Address"
                    value={editedContact.company?.address}
                    onChange={(v) => setEditedContact(p => ({ ...p, company: { ...p.company, address: v } }))}
                    disabled={!isEditing}
                  />
                </div>
              </CollapsibleSection>
            </div>
          )}
        </div>

        {/* Center Column - Conversation Thread */}
        <div className="flex-1 flex flex-col min-w-0 border-r border-slate-200">
          {/* Thread Header */}
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-testid="conversation-thread-label">
              Conversation Thread
            </h2>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 bg-white">
            {messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))}
            <div ref={messageEndRef} />
          </div>

          {/* Composer */}
          <div className="border-t border-slate-200 p-3 bg-slate-50">
            {/* Channel Tabs */}
            <div className="flex items-center gap-1 mb-3">
              {Object.entries(CHANNEL_TYPES).filter(([key]) => !['call'].includes(key)).map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setActiveChannel(key)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
                      activeChannel === key
                        ? 'bg-teal-100 text-teal-700'
                        : 'text-slate-500 hover:bg-slate-100'
                    )}
                  >
                    <Icon size={12} />
                    {config.label}
                  </button>
                );
              })}
            </div>

            {/* Input */}
            <div className="flex items-end gap-2">
              <div className="flex-1 bg-white rounded-lg border border-slate-200 focus-within:border-teal-400 focus-within:ring-1 focus-within:ring-teal-400">
                {/* Email Subject Field */}
                {activeChannel === 'email' && (
                  <div className="border-b border-slate-200">
                    <input
                      type="text"
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                      placeholder="Email subject..."
                      className="w-full px-3 py-2 text-sm focus:outline-none rounded-t-lg"
                    />
                  </div>
                )}
                <textarea
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  placeholder={`Type your ${CHANNEL_TYPES[activeChannel].label.toLowerCase()} message...`}
                  className="w-full px-3 py-2 text-sm resize-none focus:outline-none rounded-lg min-h-[60px] max-h-[120px]"
                  rows={2}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <div className="flex items-center justify-between px-2 pb-2">
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                      <Paperclip size={14} className="text-slate-400" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                      <Image size={14} className="text-slate-400" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                      <Smile size={14} className="text-slate-400" />
                    </Button>
                  </div>
                  {activeChannel === 'sms' && messageInput.length > 0 && (
                    <span className="text-xs text-slate-400">
                      {messageInput.length}/160 · {Math.ceil(messageInput.length / 160)} segment
                    </span>
                  )}
                </div>
              </div>
              <Button 
                onClick={handleSendMessage}
                disabled={!messageInput.trim() || isSending || (activeChannel === 'email' && !emailSubject.trim())}
                className="h-10 px-4 bg-teal-600 hover:bg-teal-700"
              >
                {isSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </Button>
            </div>
          </div>
        </div>

        {/* Right Column - Activity Timeline */}
        <div className="w-80 bg-white flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-testid="activity-timeline-label">
              Activity Timeline
            </h2>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            {activities.length > 0 ? (
              <div className="space-y-1">
                {activities.map((activity) => (
                  <ActivityItem key={activity.id} activity={activity} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Clock className="w-10 h-10 text-slate-300 mb-3" />
                <p className="text-sm text-slate-500 font-medium">No activity yet</p>
                <p className="text-xs text-slate-400 mt-1">Activity will appear here as you interact with this contact</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformContactWorkspacePage;
